import { useRef, useState } from 'react';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  ButtonBase,
  CardContent,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography
} from '@mui/material';

// project-imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';

// assets
import { Logout } from 'iconsax-react';

// types
import { ThemeMode } from 'types/config';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const theme = useTheme();
  const intl = useIntl();

  const { logout, user, me } = useAuth();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  const initials = (user?.name ?? '?')
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          borderRadius: 1,
          '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' }
        }}
        aria-label={intl.formatMessage({ id: 'profile.open' })}
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>
          <Avatar alt={user?.name} src={user?.photo ?? undefined} size="sm">
            {initials}
          </Avatar>
          <Typography variant="subtitle1" sx={{ display: { xs: 'none', md: 'block' } }}>
            {user?.name}
          </Typography>
        </Stack>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: 280 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MainCard content={false} border={false}>
                  <CardContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar alt={user?.name} src={user?.photo ?? undefined}>
                        {initials}
                      </Avatar>
                      <Stack spacing={0.5} sx={{ overflow: 'hidden' }}>
                        <Typography variant="subtitle1" noWrap>
                          {user?.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {user?.email ?? '—'}
                        </Typography>
                      </Stack>
                    </Stack>

                    {me?.platformRole && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={me.platformRole === 'owner' ? 'warning' : 'primary'}
                        label={intl.formatMessage({ id: `role.${me.platformRole}` })}
                        sx={{ mt: 1.5 }}
                      />
                    )}

                    {user && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5, wordBreak: 'break-all' }}>
                        <FormattedMessage id="profile.identityId" values={{ id: user.identityId }} />
                      </Typography>
                    )}
                  </CardContent>

                  <List component="nav" sx={{ p: 1, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                    <ListItemButton onClick={() => logout()}>
                      <ListItemIcon>
                        <Logout variant="Bulk" size={18} />
                      </ListItemIcon>
                      <ListItemText primary={<FormattedMessage id="profile.logout" />} />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
